// ── EVIDENCE P1.4 — the words for captured evidence, defined once ──────────────────────────────
//
// ⚠ THIS IS THE C8 SURFACE-HALF. Until P1.4 the customer app contained NO integrity claim of any
//   kind about evidence — so every sentence here is the first, and the first one that over-claims
//   is the one that sticks. The claim itself is not written here: it comes from @grc/shared, which
//   the capture path also reads, so surface and engine cannot drift.
//
// ⚠ THREE SIGNALS THAT MUST NOT BE CONFLATED, and the copy keeps them apart on purpose:
//     1. RECENCY   — how old the capture is, against the CATALOGUE's suggested cadence. Advice.
//     2. CHANGE    — whether the file changed since we captured it. A fact from a check.
//     3. HEALTH    — whether we can still reach the library at all.
//   A surface that merges any two of these tells a compliance reader something untrue.

import {
  INTEGRITY_CLAIM, INTEGRITY_CLAIM_VERSION_ONLY, INTEGRITY_SHORT, INTEGRITY_SHORT_VERSION_ONLY,
  integrityClaimFor, integrityShortFor,
  // §5.1e — the container noun, one table, so the panel never branches on a provider.
  SHAREPOINT_REPO_KIND, containerNoun, containerNounCapitalised,
} from '@grc/shared';
import type {
  ConnectorHealthState, EvidenceArtifact, EvidenceAvailability, IntegrityChangeKind,
} from '@/api/types/evidence-artifacts.types';

export { INTEGRITY_CLAIM, INTEGRITY_CLAIM_VERSION_ONLY, INTEGRITY_SHORT, INTEGRITY_SHORT_VERSION_ONLY };

/** ⚠ NEVER "expired", "valid until" or "no expiry" — the model has no validity window to speak of. */
export const RECENCY_LABEL = {
  due: 'Review recommended',
  within: 'Within recommended review cadence',
  /** ⚠ The catalogue gave no interval — so we say so, rather than implying nothing is due. */
  none: 'No recommended review cadence',
} as const;

/**
 * The connection line for one binding, in ITS provider's vocabulary.
 *
 * ⚠ "Library" WAS HARDCODED HERE AND A BOX TENANT READ IT. Two of these four states named
 *   SharePoint's container in a sentence shown for every provider — a confidently wrong word about
 *   where the tenant's evidence lives. The noun now comes from the registry
 *   (`containerNoun(repoKind)`), so Box reads "Folder reachable" and an unrecognised provider reads
 *   the neutral "Location reachable" rather than a guess.
 *
 * ⚠ SHAREPOINT'S TWO STRINGS ARE DERIVED FROM THE SAME FUNCTION, NOT KEPT BESIDE IT. Re-hardcoding
 *   them "so they cannot change" is exactly how the Box copy would drift from the shipped wording
 *   without anything failing. `HEALTH_LABEL` below is this function applied to SharePoint, which is
 *   the `INTEGRITY_SHORT = integrityShortFor(SHAREPOINT_HASH_ALGORITHM)` guarantee.
 *
 * ⓘ The other two states carry no container noun at all, and that is deliberate — `degraded` is
 *   about the FILES and `credential_expired` is about the CONNECTION, so neither gains anything
 *   from naming the container and both would read worse for it.
 */
export function healthLabelFor(state: ConnectorHealthState, repoKind: string | null): string {
  const Noun = containerNounCapitalised(repoKind);
  switch (state) {
    case 'healthy':     return `${Noun} reachable`;
    // ⚠ "could not be read", not "unreadable": it describes what happened at the last check, not a
    //   permanent property of the file.
    case 'degraded':    return 'Some evidence could not be read';
    case 'access_lost': return `${Noun} access lost`;
    // ⚠ Kept distinct from access_lost throughout: re-AUTHENTICATE is a different action from re-GRANT.
    case 'credential_expired': return 'Connection needs re-authentication';
  }
}

/**
 * ⚠ THE SHIPPED SHAREPOINT WORDING, DERIVED. Byte-identical to what tenants have always read,
 *   because it is the same function — not a second copy that happens to agree today.
 */
export const HEALTH_LABEL: Record<ConnectorHealthState, string> = {
  healthy: healthLabelFor('healthy', SHAREPOINT_REPO_KIND),
  degraded: healthLabelFor('degraded', SHAREPOINT_REPO_KIND),
  access_lost: healthLabelFor('access_lost', SHAREPOINT_REPO_KIND),
  credential_expired: healthLabelFor('credential_expired', SHAREPOINT_REPO_KIND),
};

/** ⚠ NULL health is its own state. It is NOT healthy, and it must never render as blank. */
// ⚠ "Connection health", not bare "Health": beside an evidence row, "Health not checked yet" invites
//   the reading that the EVIDENCE was not checked. What has not been checked is the connection.
export const HEALTH_NEVER_CHECKED = 'Connection health not checked yet';

/**
 * ⚠ THE CARD GROUPS ITS ROWS BY DIMENSION, and the names live here so the summary and the detail
 *   cannot drift apart. Three questions, three headings — never one blended list where a reader has
 *   to infer which signal a row belongs to.
 */
export const EVIDENCE_GROUP = {
  freshness: 'Freshness',
  integrity: 'Integrity',
  connection: 'Connection',
} as const;

/**
 * What the last check found, in the binding's own provider vocabulary.
 *
 * ⚠ TWO OF THESE NAMED SHAREPOINT'S CONTAINER — `moved` and `deleted_at_source` — and both are
 *   exactly the rows a compliance reader acts on: evidence that moved, and evidence that is gone.
 *   Telling a Box tenant their file left "the library" describes a place they do not use.
 *
 * ⓘ The other six are provider-neutral already and are returned unchanged; adding a noun to them
 *   would be inventing detail, not fixing vocabulary.
 */
export function changeLabelFor(kind: IntegrityChangeKind, repoKind: string | null): string {
  const noun = containerNoun(repoKind);
  switch (kind) {
    case 'captured':  return 'Captured — not re-checked since';
    case 'unchanged': return 'Checked — unchanged since capture';
    // ⚠ The CONSEQUENCE is paired with the FACT, in one phrase, everywhere it appears — "required",
    //   not "needed", and never the bare fact on its own.
    case 'content_changed':  return 'Changed since capture — re-review required';
    case 'version_changed':  return 'New version since capture — re-review required';
    case 'metadata_changed': return 'Details changed since capture (content unchanged)';
    case 'moved':            return `Moved in the ${noun} since capture`;
    case 'inaccessible':     return 'Could not be read at the last check';
    case 'deleted_at_source': return `No longer in the ${noun}`;
  }
}

// ── ERI S2 — current availability (D-ERI-8, D-ERI-15) ────────────────────────────────────────────

/** The two availability states that are NOT current — each names the existing change label. */
export type UnavailableKind = 'deleted_at_source' | 'inaccessible';

/**
 * Which not-current state the api projected, or null when the artifact is current.
 *
 * ⚠ READS THE PROJECTION, NEVER `latest_check`. The rule ("newest integrity row") lives once, in
 *   cts_evidence_availability (V1084); this only maps its two not-current answers to a label key.
 *   NULL availability (the api could not see the artifact) is not current either, but it has no
 *   label of its own, so it is neither counted nor labelled here.
 */
export function unavailableKind(availability: EvidenceAvailability | null): UnavailableKind | null {
  return availability === 'deleted_at_source' || availability === 'inaccessible' ? availability : null;
}

/** ERI S2 — the Overview card's two count rows. The only new copy in this slice (D-ERI-8). */
export const AVAILABILITY_ROW_LABEL: Record<UnavailableKind, string> = {
  deleted_at_source: 'Removed',
  inaccessible: 'Unreadable',
};

/**
 * The existing automated-capture wording, extracted unchanged so every surface that shows a
 * capture's actor says the same thing when there is no user (a platform-performed capture, D-ERI-7).
 */
export const CAPTURED_BY_VERIGRC = 'captured by VeriGRC';

/**
 * ERI S2 C1 — a USER performed the capture, but the api has no name for them (users.first_name /
 * last_name are nullable). ⚠ NEVER "captured by VeriGRC": crediting the platform with a person's act
 * is the misattribution D-ERI-7 removed. The only new copy in this fix.
 */
export const CAPTURED_BY_A_USER = 'captured by a user';

/**
 * Who captured this, for a surface that shows a capture's actor — decided from actor PRESENCE (the
 * api's `*_by_platform` flag), never from whether a name happens to be present.
 *
 *   platform        → CAPTURED_BY_VERIGRC
 *   user with name  → { name }        (the caller renders it as it always has)
 *   user, no name   → CAPTURED_BY_A_USER
 */
export function captureActor(platform: boolean, name: string | null):
  { kind: 'platform' | 'unnamed_user'; phrase: string } | { kind: 'named_user'; name: string } {
  if (platform) return { kind: 'platform', phrase: CAPTURED_BY_VERIGRC };
  if (name) return { kind: 'named_user', name };
  return { kind: 'unnamed_user', phrase: CAPTURED_BY_A_USER };
}

/** @see HEALTH_LABEL — the shipped SharePoint wording, derived from the same function. */
export const CHANGE_LABEL: Record<IntegrityChangeKind, string> = {
  captured: changeLabelFor('captured', SHAREPOINT_REPO_KIND),
  unchanged: changeLabelFor('unchanged', SHAREPOINT_REPO_KIND),
  content_changed: changeLabelFor('content_changed', SHAREPOINT_REPO_KIND),
  version_changed: changeLabelFor('version_changed', SHAREPOINT_REPO_KIND),
  metadata_changed: changeLabelFor('metadata_changed', SHAREPOINT_REPO_KIND),
  moved: changeLabelFor('moved', SHAREPOINT_REPO_KIND),
  inaccessible: changeLabelFor('inaccessible', SHAREPOINT_REPO_KIND),
  deleted_at_source: changeLabelFor('deleted_at_source', SHAREPOINT_REPO_KIND),
};

export const SOURCE_LABEL = {
  tenant_hosted: 'Your repository',
  /** Reserved: the Fabric slice is not built. The badge exists so no row is ever unlabelled. */
  fabric: 'Automated check',
} as const;

export const NO_EVIDENCE =
  'No evidence captured yet.';

/** ⚠ The empty state's REASON, and it is honest about all of it. */
export const NO_EVIDENCE_DETAIL =
  'When evidence is captured from this control\'s folder, it will appear here with what it looked '
  + 'like at capture.';

/**
 * The recency line: age, the catalogue's own word, and the recommendation — in that order.
 *
 * ⚠ `review_recommended === null` is NOT "nothing is due". It means the catalogue's cadence is not a
 *   period ('As Needed', 'Continuous', 'Event-Driven'), so no recommendation can be derived. Saying
 *   "within cadence" there would invent advice the catalogue never gave.
 */
export function recencyLabel(a: EvidenceArtifact): string {
  if (a.review_recommended === null) return RECENCY_LABEL.none;
  return a.review_recommended ? RECENCY_LABEL.due : RECENCY_LABEL.within;
}

/** "Captured 92 days ago · catalogue suggests Quarterly" — the cadence attributed, never asserted. */
export function capturedLine(a: EvidenceArtifact): string {
  const age = a.captured_age_days === null
    ? 'Captured'
    : a.captured_age_days === 0 ? 'Captured today' : `Captured ${a.captured_age_days} days ago`;
  return a.recommended_cadence
    ? `${age} · catalogue suggests ${a.recommended_cadence}`
    : age;
}

/**
 * The integrity line for one artifact.
 *
 * ⚠ NO TICK, NO "VERIFIED". It names the algorithm every time, because the algorithm is what makes
 *   the claim readable: a checksum detects change, it does not prove absence of change.
 *
 * ⚠ THE CLAIM COMES FROM THIS ARTIFACT'S OWN ALGORITHM, NOT A CONSTANT. It used to read
 *   `${INTEGRITY_SHORT} · ${a.hash_algorithm}` — a hardcoded quickXorHash phrase with the stored
 *   algorithm appended — so a Box artifact rendered
 *   "Change-detection (quickXorHash, non-cryptographic) · sha1": two algorithms named at once, the
 *   wrong one first, in a compliance-evidence product. The composition shape is unchanged so the
 *   SharePoint line stays byte-identical; only the phrase is now the artifact's own.
 */
export function integrityLine(a: EvidenceArtifact): string {
  // ⚠ The weaker case gets its OWN words, not a shortened version of the stronger one.
  if (!a.hash_algorithm) return INTEGRITY_SHORT_VERSION_ONLY;
  return `${integrityShortFor(a.hash_algorithm)} · ${a.hash_algorithm}`;
}

/**
 * The full claim for ONE artifact.
 *
 * ⚠ AN ARTIFACT WITH NO HASH MUST NOT CARRY A CHECKSUM CLAIM. That sentence describes a checksum
 *   comparison, and for this file no checksum exists to compare — presenting it would describe a
 *   check that never happened.
 */
export function integrityClaim(a: EvidenceArtifact): string {
  return a.hash_algorithm ? integrityClaimFor(a.hash_algorithm) : INTEGRITY_CLAIM_VERSION_ONLY;
}
